import { useSearchParams } from "react-router-dom"
import { useGet } from "utils/useFetch"
import { IReviewCycles } from "../../types"
import { Select } from "antd"
import getIdFromKey from "utils/getIdFromKey"
import {
  useCycleDurationAdminStore,
  SelectedCDDataAdmin,
} from "stores/cycle-duration"

export type { SelectedCDDataAdmin }

interface CycleDurationSelectAdminProps {
  className?: string
  value?: string | number
  onChange?: (value: string | number) => void
}

const CycleDurationSelectAdmin = ({
  className = "",
  value,
  onChange,
}: CycleDurationSelectAdminProps) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const initialCdKey = searchParams.get("cd") || ""
  const initialCdId = getIdFromKey(initialCdKey)

  const { selectedCDData, setSelectedCDData } = useCycleDurationAdminStore()

  const { isLoading: isCyclesLoading, data: cycles } = useGet<IReviewCycles>({
    url: "/api/v2/admin/reviewcycles/reviewcycledetails",
    instanceKey: "newArchAxiosInstance",
    onSuccess(data: IReviewCycles) {
      const allDurations = data?.data.docs.flatMap(
        (i) => i.cycleDurationsResponse || []
      )

      // Prefer regular cycles; if none exists, use the first off-cycle.
      const regularCycleDuration = data?.data.docs.find((i) => {
        return i.reviewCycleTypeId !== 4
      })

      const firstOffCycleDuration = data?.data.docs.find((i) => {
        return i.reviewCycleTypeId === 4
      })

      const cycleDurationForDefault =
        regularCycleDuration || firstOffCycleDuration

      const activeDuration =
        cycleDurationForDefault?.cycleDurationsResponse?.find(
          (i) => i.timeStatus === "Active" || i.timeStatus === "Past"
        )

      const fallbackDuration =
        cycleDurationForDefault?.cycleDurationsResponse?.[0]

      const defaultDuration = activeDuration || fallbackDuration

      const hasStoredValue =
        !!useCycleDurationAdminStore.getState().selectedCDData.id
      const hasCdParam = !!searchParams.get("cd")

      // If store already has a persisted selection, enrich it with start/end dates if needed
      if (hasStoredValue && !hasCdParam) {
        const stored = useCycleDurationAdminStore.getState().selectedCDData
        const matchedDuration = allDurations.find(
          (option) => option?.cycleDurationId?.toString() === stored.id
        )
        if (matchedDuration) {
          setSelectedCDData({
            ...stored,
            startDate: matchedDuration?.startDate,
            endDate: matchedDuration?.endDate,
            frameworkType: matchedDuration?.frameworkType as 0 | 1 | 2 | 3,
            frameworkTypeDescription:
              matchedDuration?.frameworkTypeDescription as
                | "None"
                | "ObjectiveLed"
                | "PersonCentered"
                | "BalancedScorecard",
          })
          if (stored.id) {
            onChange?.(stored.id)
          }
          setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev)
            newParams.set("cd", stored.key || matchedDuration.cycleDurationKey)
            return newParams
          })
        }
        return
      }

      // If cd in search params, use that
      if (hasCdParam) {
        const cdKey = searchParams.get("cd") || ""
        const cdId = getIdFromKey(cdKey)

        const matchedDuration = allDurations.find(
          (option) => option?.cycleDurationId?.toString() === cdId
        )

        setSelectedCDData({
          id: cdId,
          key: cdKey,
          startDate: matchedDuration?.startDate,
          endDate: matchedDuration?.endDate,
          frameworkType: matchedDuration?.frameworkType as 0 | 1 | 2 | 3,
          frameworkTypeDescription:
            matchedDuration?.frameworkTypeDescription as
              | "None"
              | "ObjectiveLed"
              | "PersonCentered"
                | "BalancedScorecard",
        })
        if (cdId) {
          onChange?.(cdId)
        }
        return
      }

      // No stored value and no cd param — set to default
      if (defaultDuration) {
        const defaultId = defaultDuration.cycleDurationId?.toString() || ""
        setSelectedCDData((prev) => ({
          ...prev,
          id: defaultId,
          key: defaultDuration.cycleDurationKey,
          frameworkType: defaultDuration.frameworkType as 0 | 1 | 2 | 3,
          frameworkTypeDescription: defaultDuration.frameworkTypeDescription as
            | "None"
            | "ObjectiveLed"
            | "PersonCentered"
                | "BalancedScorecard",
        }))
        if (defaultId) {
          onChange?.(defaultId)
        }
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev)
          newParams.set("cd", defaultDuration.cycleDurationKey)
          return newParams
        })
      }
    },
  })

  const options = cycles?.data.docs.flatMap((i) => {
    return i.cycleDurationsResponse
  })
  const selectOptions = [
    ...((options || []).map((period) => ({
      label: period?.reviewCycleDurationName,
      value: period?.cycleDurationId?.toString(),
    })) || []),
    {
      label: " ",
      value: "__empty-option-1",
      disabled: true,
      className: "pointer-events-none",
    },
    {
      label: " ",
      value: "__empty-option-2",
      disabled: true,
      className: "pointer-events-none",
    },
  ]

  const frameworkBadgeMap = {
    PersonCentered: {
      label: "Person-led",
      dot: "bg-blue-400",
      pill: "bg-blue-50 text-blue-600 border border-blue-200",
    },
    ObjectiveLed: {
      label: "Objective-led",
      dot: "bg-amber-400",
      pill: "bg-amber-50 text-amber-600 border border-amber-200",
    },
    BalancedScorecard: {
      label: "Balanced Scorecard",
      dot: "bg-violet-400",
      pill: "bg-violet-50 text-violet-600 border border-violet-200",
    },
  } as const

  const badge =
    selectedCDData.frameworkTypeDescription &&
    selectedCDData.frameworkTypeDescription !== "None"
      ? frameworkBadgeMap[
          selectedCDData.frameworkTypeDescription as keyof typeof frameworkBadgeMap
        ]
      : null

  return (
    <div className="inline-flex gap-1 p-1 bg-[#F3F5FB] rounded-5px">
      <Select
        value={
          isCyclesLoading
            ? undefined
            : value !== undefined && value !== null
            ? value?.toString()
            : selectedCDData.id || initialCdId
        }
        onChange={(id) => {
          const matched = options?.find(
            (option) => option?.cycleDurationId?.toString() === id?.toString()
          )
          const selectedId = id?.toString() || ""
          setSelectedCDData({
            id: selectedId,
            key: matched?.cycleDurationKey,
            startDate: matched?.startDate,
            endDate: matched?.endDate,
            frameworkType: matched?.frameworkType as 0 | 1 | 2 | 3,
            frameworkTypeDescription: matched?.frameworkTypeDescription as
              | "None"
              | "ObjectiveLed"
              | "PersonCentered"
                | "BalancedScorecard",
          })

          onChange?.(selectedId)

          setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev)
            newParams.set("cd", matched?.cycleDurationKey || id)
            return newParams
          })
        }}
        className={`w-[150px] h-full ${className}`}
        allowClear={false}
        popupClassName="font-avenir"
        loading={isCyclesLoading}
        showSearch
        filterOption={(input, option) =>
          (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
        }
        options={selectOptions}
      />
      {badge && (
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium w-fit font-avenir ${badge.pill} flex-shrink-0`}
        >
          <span
            className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${badge.dot}`}
          />
          {badge.label}
        </span>
      )}
    </div>
  )
}

export default CycleDurationSelectAdmin
